<div class="my-requests-container">
  <div class="header">
    <div class="header-content">
      <div class="header-text">
        <h1>My Requests</h1>
        <p class="subtitle">Manage and track your event requests. Only unsubmitted requests can be deleted, to cancel submitted events please contact the Broadcast team</p>
      </div>
    </div>
  </div>

  <!-- Loading Spinner -->
  <div *ngIf="loading" class="loading-container">
    <mat-spinner diameter="50"></mat-spinner>
    <p class="loading-text">Loading your requests...</p>
  </div>

  <!-- No User Message -->
  <div *ngIf="!loading && !user" class="no-user-container">
    <mat-icon class="large-icon">person_off</mat-icon>
    <h2>User Not Found</h2>
    <p>Please log in to view your requests.</p>
  </div>

  <!-- Main Content -->
  <div *ngIf="!loading && user" class="content">
    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card unsubmitted" 
           [class.active]="isFilterActive('unsubmitted')"
           (click)="filterByUnsubmitted()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>schedule</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ getUnsubmittedCount() }}</h3>
            <p>Unsubmitted</p>
          </div>
        </div>
      </div>

      <div class="summary-card pending" 
           [class.active]="isFilterActive('pending')"
           (click)="filterByPending()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>schedule</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ getPendingCount() }}</h3>
            <p>Pending</p>
          </div>
        </div>
      </div>

      <div class="summary-card approved"
           [class.active]="isFilterActive('approved')"
           (click)="filterByApproved()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>check_circle</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ getApprovedCount() }}</h3>
            <p>Approved</p>
          </div>
        </div>
      </div>

      <div class="summary-card completed"
           [class.active]="isFilterActive('completed')"
           (click)="filterByCompleted()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>check_circle</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ getCompletedCount() }}</h3>
            <p>Completed</p>
          </div>
        </div>
      </div>

      <div class="summary-card rejected"
           [class.active]="isFilterActive('canceled')"
           (click)="filterByCanceled()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>cancel</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ getCanceledCount() }}</h3>
            <p>Canceled</p>
          </div>
        </div>
      </div>

      <div class="summary-card total"
           [class.active]="isFilterActive('all')"
           (click)="filterByAll()">
        <div class="card-content">
          <div class="card-icon">
            <mat-icon>event_note</mat-icon>
          </div>
          <div class="card-info">
            <h3>{{ myRequests.length }}</h3>
            <p>Total Requests</p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Requests Message -->
    <div *ngIf="filteredRequests.length === 0 && myRequests.length === 0" class="no-requests-container">
      <mat-icon class="large-icon">event_busy</mat-icon>
      <h2>No Requests Found</h2>
      <p>You haven't submitted any event requests yet.</p>
      <button mat-raised-button color="primary" routerLink="/request" class="create-request-button">
        <mat-icon>add</mat-icon>
        Create New Request
      </button>
    </div>

    <!-- No Filtered Results Message -->
    <div *ngIf="filteredRequests.length === 0 && myRequests.length > 0" class="no-requests-container">
      <mat-icon class="large-icon">filter_list_off</mat-icon>
      <h2>No {{ activeFilter === 'all' ? '' : activeFilter }} Requests Found</h2>
      <p>Try selecting a different filter or <span (click)="filterByAll()" class="filter-link">view all requests</span>.</p>
    </div>

    <!-- Requests List -->
    <div *ngIf="filteredRequests.length > 0" class="requests-section">
      <div class="section-header">
        <h2>Your Event Requests 
          <span *ngIf="activeFilter !== 'all'" class="filter-indicator">
            ({{ activeFilter }} - {{ filteredRequests.length }} of {{ myRequests.length }})
          </span>
        </h2>
        <div class="action-buttons">
          <button class="refresh-button" 
                  (click)="refreshRequests()" 
                  [disabled]="loading"
                  mat-raised-button>
            <mat-icon>refresh</mat-icon>
            Refresh
          </button>
          <button mat-raised-button color="primary" routerLink="/request" class="create-request-button">
            <mat-icon>add</mat-icon>
            Create New Request
          </button>
        </div>
      </div>

      <div class="requests-grid">
        <div class="event-legend" *ngIf="filteredRequests.length > 0">
          <span class="legend-item past">Past</span>
          <span class="legend-item today">Today</span>
          <span class="legend-item future">Upcoming</span>
        </div>

        <div *ngFor="let request of filteredRequests; trackBy: trackByRequestId" 
             class="request-card"
             [class.future-event]="isFutureRequest(request) && !isRequestToday(request)"
             [class.today-event]="isRequestToday(request)"
             [class.past-event]="!isFutureRequest(request) && !isRequestToday(request)"
             (click)="editRequest(request)">
          
          <!-- Request Header -->
          <div class="request-header">
            <div class="request-title">
              <h3>{{ request.displayTitle }}</h3>
              <span class="status-badge" [ngClass]="getStatusClass(request.status)">
                {{ getDisplayStatus(request.status) }}
              </span>
            </div>
            <div class="request-actions">
              <button mat-icon-button 
                      *ngIf="request.status?.toLowerCase() == 'unsubmitted'"
                      (click)="deleteRequest(request)"
                      matTooltip="Delete Request"
                      class="delete-button">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </div>

          <!-- Request Details -->
          <div class="request-details">
            <div class="detail-row">
              <mat-icon class="detail-icon">event</mat-icon>
              <div class="detail-content">
                <span class="detail-label">Date & Time:</span>
                <span class="detail-value">{{ request.displayDate }} <span class="tz-inline">ET</span></span>
                <span class="detail-time">{{ request.displayTime }} <span class="tz-inline">ET</span></span>
                <span *ngIf="getRelativeTimeDescription(request)" 
                      class="relative-time">{{ getRelativeTimeDescription(request) }}</span>
              </div>
            </div>

            <div class="detail-row" *ngIf="request.location">
              <mat-icon class="detail-icon">location_on</mat-icon>
              <div class="detail-content">
                <span class="detail-label">Location:</span>
                <span class="detail-value">{{ request.location }}</span>
              </div>
            </div>

            <div class="detail-row" *ngIf="request.audience_size">
              <mat-icon class="detail-icon">group</mat-icon>
              <div class="detail-content">
                <span class="detail-label">Expected Audience:</span>
                <span class="detail-value">{{ request.audience_size }}</span>
              </div>
            </div>

            <div class="detail-row" *ngIf="request.request_description">
              <mat-icon class="detail-icon">description</mat-icon>
              <div class="detail-content">
                <span class="detail-label">Description:</span>
                <p class="detail-description">{{ request.request_description }}</p>
              </div>
            </div>
          </div>

          <!-- Request Footer -->
          <div class="request-footer">
            <div class="submission-info">
              <span class="submitted-date" *ngIf="request.created_at">
                Submitted: {{ formatDate(request.created_at) }}
              </span>
            </div>
            <div class="footer-actions">
              <button class="metrics-button" *ngIf="hasMetrics(request)" (click)="openMetricsDialog(request, $event)">
                <mat-icon>bar_chart</mat-icon>
                Metrics
              </button>
              <button class="delete-icon-button" *ngIf="user?.isAdmin || user?.isOwner" (click)="deleteEvent(request, $event)" matTooltip="Delete Event">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            <div class="request-id" *ngIf="request.event_id">
              <span class="id-label">ID: #{{ request.event_id }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
